/**
 * GenerationBreakdownTable — compact per-fuel generation table that sits beside
 * the GenerationChangeChart bar chart.
 *
 * Columns:
 *   Generation Type | Historical | Difference | Interconnection
 *
 *   - Historical      = generation in the project's own balancing authority (BA)
 *   - Difference      = TOTAL displacement (local BA + interconnection)
 *   - Interconnection = the portion of that displacement occurring in
 *                       NEIGHBORING balancing authorities (negative = displaced,
 *                       matching the Difference sign convention). Sourced from
 *                       the run's interconnection_breakdown; "—" when a fuel has
 *                       no cross-border displacement or the run carries no
 *                       breakdown at all.
 */

import { useMemo } from 'react';
import {
  getFuelLabel,
  interconnectionByCanonicalFuel,
  interconnectionDisplacementForFuel,
} from '../domain/definitions';

interface GenerationValues {
  Historical?: number;
  Projected?: number;
  Difference?: number;
  [key: string]: number | string | undefined;
}

interface InterconnectionBreakdownLike {
  neighbors?: Array<{ displaced_generation?: Record<string, number> | null } | null> | null;
}

interface GenerationBreakdownTableProps {
  generationData: Record<string, GenerationValues>;
  interconnectionBreakdown?: InterconnectionBreakdownLike | null;
  baCode?: string;
  profileLabel?: string;
}

function fmtGwh(v: number): string {
  return v.toFixed(1);
}

export function GenerationBreakdownTable({
  generationData,
  interconnectionBreakdown,
  baCode,
  profileLabel,
}: GenerationBreakdownTableProps) {
  const rows = useMemo(() => {
    const byFuel = interconnectionByCanonicalFuel(interconnectionBreakdown);
    return Object.entries(generationData)
      .map(([fuel, values]) => {
        const historical = typeof values.Historical === 'number' ? values.Historical : null;
        const difference = typeof values.Difference === 'number' ? values.Difference : 0;
        const interconnection = interconnectionDisplacementForFuel(fuel, byFuel);
        return { fuel, label: getFuelLabel(fuel), historical, difference, interconnection };
      })
      // Keep any fuel that has meaningful historical or difference data.
      .filter((r) => (r.historical != null && Math.abs(r.historical) > 0.001) || Math.abs(r.difference) > 0.001)
      .sort((a, b) => b.difference - a.difference);
  }, [generationData, interconnectionBreakdown]);

  const hasInterconnection = rows.some((r) => r.interconnection !== 0);

  if (rows.length === 0) return null;

  return (
    <div className="mt-4 rounded-xl bg-surface-700 border border-border p-6">
      <h3 className="text-lg font-semibold text-text-primary mb-1">
        Generation by Fuel Type
      </h3>
      <p className="text-xs text-text-secondary mb-4">
        Change in grid generation by fuel type with this project (GWh
        {profileLabel ? `, ${profileLabel}` : ''})
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/60">
              <th className="text-left py-2 px-2 text-text-muted font-medium">Generation Type</th>
              <th className="text-right py-2 px-2 text-text-muted font-medium">
                Historical{baCode ? ` (BA: ${baCode})` : ''}
              </th>
              <th className="text-right py-2 px-2 text-text-muted font-medium">Difference</th>
              <th className="text-right py-2 px-2 text-text-muted font-medium">Interconnection</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.fuel}
                className="border-b border-border/30 hover:bg-surface-600/40 transition-colors"
              >
                <td className="py-2 px-2 text-text-primary font-medium">{r.label}</td>
                <td className="py-2 px-2 text-right text-text-secondary tabular-nums">
                  {r.historical != null ? fmtGwh(r.historical) : '—'}
                </td>
                <td className="py-2 px-2 text-right text-text-primary tabular-nums">
                  {fmtGwh(r.difference)}
                </td>
                <td className="py-2 px-2 text-right text-text-secondary tabular-nums">
                  {r.interconnection !== 0 ? fmtGwh(r.interconnection) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11px] text-text-muted leading-snug">
        Historical reflects generation in the project&rsquo;s own balancing authority
        {baCode ? ` (${baCode})` : ''}. Difference is the total displacement;
        Interconnection is the portion of that displacement occurring in neighboring
        balancing authorities{hasInterconnection ? '' : ' (none for this project)'}.
      </p>
    </div>
  );
}
